import { forwardRef } from 'react';
import { Divider as MuiDivider, DividerProps as MuiDividerProps } from '@mui/material';

/**
 * Divider component - A thin line that separates content sections.
 *
 * This is a thin wrapper around Material-UI's Divider component that provides:
 * - Horizontal and vertical orientations
 * - Multiple variants (fullWidth, inset, middle)
 * - Theme integration with divider color
 * - Flexible layout support (flexItem)
 * - Full accessibility (role="separator")
 *
 * Common use cases:
 * - Separating sections in settings pages
 * - Dividing Widget header from content
 * - Visual separation in lists
 * - Layout organization in forms
 *
 * @example
 * // Simple horizontal divider (most common pattern)
 * <Divider />
 *
 * @example
 * // Widget header separator (from Widget.tsx)
 * {hasHeader && divider && <Divider />}
 *
 * @example
 * // Vertical divider in flexbox (requires flexItem)
 * <Stack direction="row" spacing={2}>
 *   <Button>Left</Button>
 *   <Divider orientation="vertical" flexItem />
 *   <Button>Right</Button>
 * </Stack>
 *
 * @example
 * // Settings section separator (from SettingsForm.tsx)
 * <Typography variant="h3">Section Title</Typography>
 * <Divider />
 * <Typography>Section content...</Typography>
 *
 * @example
 * // Inset divider in list
 * <Divider variant="inset" />
 */
export type DividerProps = MuiDividerProps;

export const Divider = forwardRef<HTMLHRElement, DividerProps>((props, ref) => (
  <MuiDivider ref={ref} {...props} />
));

Divider.displayName = 'Divider';
